import React, {
  ReactNode,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import cx from 'classnames';

import Icon from '@atlaskit/icon';
import { Popper } from '@atlaskit/popper';
import Portal from '@atlaskit/portal';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import {
  ELEVATION_ATTR,
  useCallbackRef,
  useCurrentElevation,
  useClickOutsideHandler,
} from '@trello/layer-manager';
import { PopoverTestIds } from '@trello/webdriverio-tests';

import {
  GLOBAL_NAMESPACE_PREFIX,
  ComponentClassnameStatic,
  ComponentSizeL as LARGE,
  ComponentSizeM as MEDIUM,
  ComponentSizeS as SMALL,
  ComponentSizeXl as XLARGE,
  PopoverClassnameBase,
  PopoverClassnameBackButton,
  PopoverClassnameCloseButton,
  PopoverClassnameContent,
  PopoverClassnameHeader,
  PopoverClassnameIconButton,
  PopoverClassnameTitle,
  PopoverPortalDefaultZIndex,
  PopoverLargeWidth,
  PopoverMediumWidth,
  PopoverPopperDefaultPaddingUnit,
  PopoverContentDefaultPadding,
  PopoverSmallWidth,
  PopoverXlargeWidth,
} from '../../../tokens';

import { PopoverContext } from './PopoverScreen';
import styles from './Popover.less';
import { usePrevious } from './usePopover';

// trello specific hack so that we can tell the popper API the bounds of where
// want the popovers to render (the area below the header, that includes things
// like banners)
export const POPOVER_BOUNDARY_ELEMENT_ID = 'popover-boundary';

export const PopoverClasses = {
  POPOVER: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}`,
  HEADER: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameHeader}`,
  TITLE: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameTitle}`,
  ICON_BUTTON: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameIconButton}`,
  CLOSE_BUTTON: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameCloseButton}`,
  BACK_BUTTON: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameBackButton}`,
  CONTENT: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}__${PopoverClassnameContent}`,
  STATIC: `${GLOBAL_NAMESPACE_PREFIX}${PopoverClassnameBase}--${ComponentClassnameStatic}`,
};

const popoverSizeValues = {
  [SMALL]: PopoverSmallWidth,
  [MEDIUM]: PopoverMediumWidth,
  [LARGE]: PopoverLargeWidth,
  [XLARGE]: PopoverXlargeWidth,
};

export type PopoverSize =
  | typeof SMALL
  | typeof MEDIUM
  | typeof LARGE
  | typeof XLARGE;

export enum PopoverPlacement {
  AUTO_START = 'auto-start',
  AUTO = 'auto',
  AUTO_END = 'auto-end',
  TOP_START = 'top-start',
  TOP = 'top',
  TOP_END = 'top-end',
  RIGHT_START = 'right-start',
  RIGHT = 'right',
  RIGHT_END = 'right-end',
  BOTTOM_START = 'bottom-start',
  BOTTOM = 'bottom',
  BOTTOM_END = 'bottom-end',
  LEFT_START = 'left-start',
  LEFT = 'left',
  LEFT_END = 'left-end',
}

export enum HideReason {
  CLICK_OUTSIDE = 'click outside the popover',
  NO_ANCHOR_ELEMENT = 'popover was rendered with isVisible = true, but no anchor element to render on',
  ESCAPE_HANDLER = 'escape keypress handler',
  CLICK_CLOSE_BUTTON = 'click on the close button',
  NAVIGATION = 'browser navigation event',
}

interface RefHandler<T = HTMLElement> {
  (node: T | null): void;
}

interface PopoverHeaderProps {
  /**
   * React node(s) to render inside the Popover header
   */
  children: ReactNode;
  /**
   * Determines whether or not the Popover should render a "Back" button. This
   * back button allows navigation between PopoverScreens (multi-screen
   * popovers)
   * @default false
   */
  hasBackButton?: boolean;
  /**
   * A callback function that fires when the "back" button in the Popover is
   * clicked
   * @type { function }
   * @default undefined
   */
  onBack?: () => void;
  /**
   * A callback function that fires when the Popover's state is changed from
   * visible to not visible
   */
  onHide: (reason: HideReason) => void;
}
interface PopoverContentProps {
  /**
   * React node(s) to render inside the Popover content area (below the header,
   * where the Popover Screens are shown)
   */
  children: ReactNode;
  /**
   * A number (measured in pixels) to limit the max height of the
   * content of the Popover.
   * This number is dynamically calculated in useResizeHandler
   * and depends on the header height (trello only), viewport height, and some
   * spacing values (e.g. padding)
   * @default 0
   */
  maxHeight?: number;
  /**
   * Removes the default horizontal padding from the popover content.
   * This is useful when you want your content to extend all the way
   * to the edge of the popover.
   * @default false
   */
  noHorizontalPadding?: boolean;
  /**
   * Removes the default top padding from the popover content. This is
   * automatically placed when there is a title in the Popover Header.
   * @default false
   */
  noTopPadding?: boolean;
  /**
   * Removes the default bottom padding from the popover content.
   * @default false
   */
  noBottomPadding?: boolean;
}

export interface PopoverProps<
  TElementTrigger = HTMLElement,
  TElementTarget = HTMLElement
> {
  // Public API
  /**
   * Determines the position of where the Popover will open relative to its
   * trigger element
   * @default 'bottom-start'
   */
  placement?: PopoverPlacement;
  /**
   * The relative width of the Popover component, eventually
   * converted into pixel measurements. If none is provided, Popover
   * will fallback to `'medium'`.
   * @default 'medium'
   */
  size?: PopoverSize;
  /**
   * The content to display in the title container of the Popover
   * @default undefined
   */
  title?: ReactNode;
  /**
   * Removes the default horizontal padding from the popover content.
   * This is useful when you want your content to extend all the way
   * to the edge of the popover.
   * @default false
   */
  noHorizontalPadding?: boolean;
  /**
   * Removes the default vertical padding from the popover content.
   * This is useful when you want your content to extend all the way
   * to the edge of the popover.
   * @default false
   */
  noVerticalPadding?: boolean;
  /**
   * A string that gets placed as a data attribute (data-test-id) onto the
   * Portal rendered inside Popover so that our
   * smoketest can properly target and test the component
   * @default undefined
   */
  testId?: string;

  // Provided by the hook
  /**
   * Indicates whether or not the Popover is currently visible
   */
  isVisible: boolean;
  /**
   * The element that will toggle the visibility of the popover.
   * Toggling visibility is usually achieved by interacting with the
   * trigger element by mouse.
   */
  triggerElement: TElementTrigger | null;
  /**
   * The element that the Popover will position itself relative to. If no target
   * element is provided, the trigger element is used.
   */
  targetElement: TElementTarget | null;
  /**
   * A callback function that fires when the Popover's state is changed from
   * visible to not visible
   */
  onHide: (reason: HideReason) => void;
  /**
   * Indicates whether or not the Popover should render a "Back" button. This
   * back button allows navigation between PopoverScreens (multi-screen
   * popovers)
   * @default false
   */
  hasBackButton?: boolean;
  /**
   * A callback function that fires when the "back" button in the Popover is
   * clicked
   * @default undefined
   */
  onBack?: () => void;
  /**
   * A number representing the current screen that is rendered within the
   * Popover. This number will typically come from an enum in order to
   * differentiate each screen as unique, such as:
   * ```
   * enum Screen {
   *  ScreenA,
   *  ScreenB,
   * }
   * ```
   * @default 0
   */
  currentScreen?: number;

  // Escape hatches
  /**
   * ⚠️ DO NOT USE THIS PROP UNLESS YOU REALLY REALLY HAVE TO.
   *
   * Places a class name on the Popover (more specifically, the direct
   * descendant of the React Portal that renders the Popover).
   *
   * Please refrain from using this unless absolutely necessary.
   * @default undefined
   */
  dangerous_className?: string;

  /**
   * ⚠️ DO NOT USE THIS PROP UNLESS YOU REALLY REALLY HAVE TO.
   *
   * Overrides the 'size' prop with a custom pixel width. Useful
   * for legacy popovers that are designed to a very specific size (eg. EmojiPicker)
   *
   * Please refrain from using this unless absolutely necessary.
   * @default undefined
   */
  dangerous_width?: number;

  /**
   * ⚠️ DO NOT USE THIS PROP UNLESS YOU REALLY REALLY HAVE TO.
   *
   * Disables any auto focusing behavior of the Popover. By default the content of the Popover
   * will be focused when it is shown, and the trigger element will be focused when it is hidden
   *
   * Please refrain from using this unless absolutely necessary.
   * @default undefined
   */
  dangerous_disableFocusManagement?: boolean;
}

/**
 * Attach a window click handler to close the Popover whenever you click outside
 * of its boundaries
 */
const useOutsideClickHandler = ({
  containerElement,
  triggerElement,
  onHide,
  isVisible,
}: {
  containerElement: HTMLElement | null;
  triggerElement: HTMLElement | null;
  onHide: (reason: HideReason) => void;
  isVisible: boolean;
}) => {
  const clickHandler = useCallback(
    (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      // Exit if we're clicking on the trigger, letting it handle toggling
      // the open state
      if (triggerElement && triggerElement.contains(event.target as Node)) {
        return;
      }

      // Finally, if our popover is visible, call our onHide callback
      if (isVisible) {
        onHide(HideReason.CLICK_OUTSIDE);
      }
    },
    [isVisible, onHide, triggerElement],
  );

  useClickOutsideHandler({
    element: containerElement,
    handleClickOutside: clickHandler,
    skip: !isVisible,
  });
};

/**
 * Attach window resize listener to update the max height of the Popover
 * content, ensuring that the height of the Popover is never greater than the
 * height of the viewport
 */
const useResizeHandler = ({
  isVisible,
  containerElement,
  contentElement,
  headerElement,
  schedulePopperUpdateRef,
}: {
  isVisible: boolean;
  containerElement: HTMLElement | null;
  contentElement: HTMLElement | null;
  headerElement: HTMLElement | null;
  schedulePopperUpdateRef: RefObject<(() => void) | undefined>;
}) => {
  const [contentMaxHeight, setContentMaxHeight] = useState<number>();
  const schedulePopperUpdate = schedulePopperUpdateRef.current;

  const resizeHandler = useCallback(() => {
    if (!containerElement || !contentElement) {
      return;
    }

    // Use the #content div as our viewport if it exists, otherwise default
    // to the whole document
    const viewportElement =
      document.getElementById(POPOVER_BOUNDARY_ELEMENT_ID) ||
      document.documentElement;
    const viewportHeight = viewportElement.clientHeight;

    const containerHeight = containerElement.getBoundingClientRect().height;
    const headerHeight = headerElement
      ? headerElement.getBoundingClientRect().height
      : 0;
    const contentHeight = contentElement.getBoundingClientRect().height;
    const internalPadding = headerElement
      ? parseInt(PopoverContentDefaultPadding)
      : parseInt(PopoverContentDefaultPadding) * 2;

    const extraPixels =
      containerHeight -
      headerHeight -
      contentHeight +
      PopoverPopperDefaultPaddingUnit * 2 + // This is the padding _external_ to popper
      internalPadding; // This is the padding _internal_ to the popover's content

    const newContentMaxHeight = viewportHeight - headerHeight - extraPixels;

    // Paranoid check for a negative max height. This would only occur if the
    // header alone was somehow bigger than the entire viewport
    if (newContentMaxHeight > 0) {
      setContentMaxHeight(newContentMaxHeight);
    } else {
      setContentMaxHeight(0);
    }
  }, [containerElement, headerElement, contentElement]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      resizeHandler();
      if (schedulePopperUpdate) {
        schedulePopperUpdate();
      }
    });

    if (isVisible) {
      resizeHandler();

      window.addEventListener('resize', resizeHandler);

      if (contentElement) {
        observer.observe(contentElement, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    }

    return () => {
      window.removeEventListener('resize', resizeHandler);
      observer.disconnect();
    };
  }, [isVisible, contentElement, resizeHandler, schedulePopperUpdate]);

  return { contentMaxHeight };
};

const useEscapeHandler = ({
  isVisible,
  hasBackButton,
  onBack,
  onHide,
}: {
  isVisible: boolean;
  hasBackButton?: boolean;
  onBack?: () => void;
  onHide: (reason: HideReason) => void;
}) => {
  // Handle escape keypress to either pop or hide
  const escapeHandler = useCallback(() => {
    if (hasBackButton && onBack) {
      onBack();
    } else {
      onHide(HideReason.ESCAPE_HANDLER);
    }
  }, [hasBackButton, onBack, onHide]);

  useShortcut(escapeHandler, {
    scope: Scope.Popover,
    key: Key.Escape,
    enabled: isVisible,
  });
};

const useFocusManagement = ({
  contentElement,
  triggerElement,
  dangerous_disableFocusManagement,
}: {
  contentElement: HTMLElement | null;
  triggerElement: HTMLElement | null;
  dangerous_disableFocusManagement?: boolean;
}) => {
  const prevContentElement = usePrevious(contentElement);

  // Auto focus the content when it is mounted
  useLayoutEffect(() => {
    if (
      !dangerous_disableFocusManagement &&
      !prevContentElement &&
      contentElement
    ) {
      contentElement.focus();
    }
  }, [contentElement, dangerous_disableFocusManagement, prevContentElement]);

  // Focus the trigger when the content is unmounted
  useLayoutEffect(() => {
    if (
      !dangerous_disableFocusManagement &&
      prevContentElement &&
      !contentElement &&
      triggerElement
    ) {
      triggerElement.focus();
    }
  }, [
    contentElement,
    dangerous_disableFocusManagement,
    prevContentElement,
    triggerElement,
  ]);
};

/**
 * Poll the current url to detect navigation if the popover is currently visible,
 * and hide the popover if it occurs. This _could_ be replaced with a mechanism
 * that 'hijacks' history.pushState and history.replaceState if we wanted to make
 * this generic, or we expected _many_ popovers to somehow be visible at the same
 * time.
 */
const useNavigationHandler = ({
  isVisible,
  onHide,
}: {
  isVisible: boolean;
  onHide: (reason: HideReason) => void;
}) => {
  const prevUrlRef = useRef<string>();
  const intervalIdRef = useRef<number>();

  useEffect(() => {
    if (isVisible) {
      const intervalId = window.setInterval(() => {
        const prevUrl = prevUrlRef.current;
        const url = window.location.href;
        prevUrlRef.current = url;

        if (prevUrl && prevUrl !== url) {
          onHide(HideReason.NAVIGATION);
        }
      }, 500);
      intervalIdRef.current = intervalId;
    }
    return () => {
      if (intervalIdRef.current) {
        window.clearInterval(intervalIdRef.current);
      }
      intervalIdRef.current = undefined;
      prevUrlRef.current = undefined;
    };
  }, [isVisible, onHide]);
};

/**
 * SVG glyph for usage with "Back" icon in Popover Header
 */
const backGlyph = () => (
  <svg
    width="10"
    height="18"
    viewBox="0 0 10 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.292893 8.29292L7.36396 1.22185C7.75449 0.831324 8.38765 0.831324 8.77817 1.22185C9.1687 1.61237 9.1687 2.24554 8.77817 2.63606L2.41421 9.00002L8.77818 15.364C9.1687 15.7545 9.1687 16.3877 8.77818 16.7782C8.38765 17.1687 7.75449 17.1687 7.36396 16.7782L0.292893 9.70713C-0.0976312 9.3166 -0.0976312 8.68344 0.292893 8.29292Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * SVG glyph for usage with "Close" icon in Popover Header
 */
const closeGlyph = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.58579 7L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683418 0.292893 0.292893C0.683418 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L7 5.58579L12.2929 0.292893C12.6834 -0.0976311 13.3166 -0.0976311 13.7071 0.292893C14.0976 0.683418 14.0976 1.31658 13.7071 1.70711L8.41421 7L13.7071 12.2929C14.0976 12.6834 14.0976 13.3166 13.7071 13.7071C13.3166 14.0976 12.6834 14.0976 12.2929 13.7071L7 8.41421L1.70711 13.7071C1.31658 14.0976 0.683418 14.0976 0.292893 13.7071C-0.0976311 13.3166 -0.0976311 12.6834 0.292893 12.2929L5.58579 7Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * A component that appears at the top of a rendered Popover. It includes the
 * title, close button, and back button (if there are multiple screens)
 */
export const PopoverHeader = forwardRef<HTMLElement, PopoverHeaderProps>(
  ({ children, hasBackButton, onBack, onHide }, ref) => {
    const onClose = useCallback(() => {
      onHide(HideReason.CLICK_CLOSE_BUTTON);
    }, [onHide]);

    return (
      <header className={styles[PopoverClasses.HEADER]} ref={ref}>
        <h1 className={styles[PopoverClasses.TITLE]}>{children}</h1>
        {hasBackButton ? (
          <button
            className={cx(
              styles[PopoverClasses.ICON_BUTTON],
              styles[PopoverClasses.BACK_BUTTON],
            )}
            onClick={onBack}
          >
            <Icon
              glyph={backGlyph}
              label="Return to previous screen"
              size="small"
            />
          </button>
        ) : null}
        <button
          className={cx(
            styles[PopoverClasses.ICON_BUTTON],
            styles[PopoverClasses.CLOSE_BUTTON],
          )}
          onClick={onClose}
          data-test-id={PopoverTestIds.ClosePopover}
        >
          <Icon glyph={closeGlyph} label="Close popover" size="small" />
        </button>
      </header>
    );
  },
);

/**
 * A component that contains the main content of the Popover. If the Popover
 * has a title, it will remove the top padding.
 * The max height is dynamically set on
 * window resize events (@see useResizeHandler)
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    { children, maxHeight, noHorizontalPadding, noTopPadding, noBottomPadding },
    ref,
  ) => (
    <div
      tabIndex={-1}
      className={cx(styles[PopoverClasses.CONTENT], {
        [styles['px-0']]: noHorizontalPadding,
        [styles['pt-0']]: noTopPadding,
        [styles['pb-0']]: noBottomPadding,
      })}
      ref={ref}
      style={{ maxHeight }}
    >
      {children}
    </div>
  ),
);

/**
 * A component that renders some content in a floating container within a
 * React Portal, typically triggered by a different element.
 * It has the ability to account for multiple screens of
 * content using hooks and context.
 * @see PopoverScreen
 * @see usePopover
 *
 * The visual state of the Popover (including it’s visibility and screen
 * management) is handled via the `usePopover` hook, which should be used
 * in conjunction with the Popover component.
 * @example
 * const { triggerRef, toggle, hide, popoverProps } = usePopover();
 *
 * return (
 *  <>
 *    <button ref={triggerRef} onClick={toggle}>
 *      Toggle popover
 *    </button>
 *    <Popover {...popoverProps}>
 *      <button onClick={hide}>Hide popover</button>
 *    </Popover>
 *  </>
 * );
 */
export const Popover: React.FunctionComponent<PopoverProps> = ({
  placement,
  size,
  title,
  noHorizontalPadding,
  noVerticalPadding,
  testId,

  isVisible,
  triggerElement,
  targetElement,
  onHide,
  hasBackButton,
  onBack,
  currentScreen,

  children,
  dangerous_className,
  dangerous_width,
  dangerous_disableFocusManagement,
}) => {
  const [screenTitle, setScreenTitle] = useState<ReactNode | undefined>(
    undefined,
  );
  const [screenSize, setScreenSize] = useState<PopoverSize | undefined>(
    undefined,
  );
  const [screenNoHorizontalPadding, setScreenNoHorizontalPadding] = useState<
    boolean | undefined
  >(undefined);
  const [screenNoVerticalPadding, setScreenNoVerticalPadding] = useState<
    boolean | undefined
  >(undefined);
  const [screenTestId, setScreenTestId] = useState<string | undefined>(
    undefined,
  );

  const [headerElement, headerRef] = useCallbackRef<HTMLElement>();
  const [contentElement, contentRef] = useCallbackRef<HTMLDivElement>();
  const [containerElement, containerRef] = useCallbackRef<HTMLElement>();
  const schedulePopperUpdateRef = useRef<(() => void) | undefined>(undefined);

  // We have to use a callback wrapper to wire up the containerRef _and_ to
  // let popper know about it with it's 'ref' render prop
  const containerRefCallback = useCallback(
    <T extends HTMLElement>(
      node: T | null,
      popperRefCallback: RefHandler<T>,
    ) => {
      containerRef(node);
      popperRefCallback(node);
    },
    [containerRef],
  );

  // Determine whether to anchor the Popover to the trigger or the target.
  // If no target ref was provided, we assume the trigger is the anchor
  const anchorElement = targetElement ? targetElement : triggerElement;

  // Defensively ensure that if the state is saying we are visible, but we have no
  // anchor, call our onHide callback (we won't be able to mount anywhere so we
  // should let our parent know we were hidden to keep their state in sync)
  // This situation only arises when a consumer is managing multiple nested popovers
  // from within the same component (i.e they are invoking the usePopover hook multiple
  // times for nested popovers). In the following scenario:
  // 1. User opens root popover
  // 2. User opens nested popover
  // 3. User clicks outside the root popover
  // 4. Nested popover is _unmounted_ (not hidden)
  // 5. User opens the root popover again
  // 6. The nested popover still has isVisible as true, but no target element to be mounted on
  useEffect(() => {
    if (!anchorElement && isVisible) {
      onHide(HideReason.NO_ANCHOR_ELEMENT);
    }
  }, [anchorElement, isVisible, onHide]);

  const currentElevation = useCurrentElevation(anchorElement);
  const nextElevation = currentElevation + 1;

  useOutsideClickHandler({
    containerElement,
    triggerElement,
    onHide,
    isVisible,
  });
  const { contentMaxHeight } = useResizeHandler({
    isVisible,
    containerElement,
    contentElement,
    headerElement,
    schedulePopperUpdateRef,
  });
  useEscapeHandler({ isVisible, hasBackButton, onBack, onHide });
  useNavigationHandler({ isVisible, onHide });
  useFocusManagement({
    contentElement,
    triggerElement,
    dangerous_disableFocusManagement,
  });

  if (!isVisible || !anchorElement) {
    return null;
  }

  // Fall-back to the 'title' and 'size' props for the Popover if none were
  // provided to the PopoverScreen
  const actualTitle = screenTitle ?? title;
  const actualSize =
    dangerous_width ?? popoverSizeValues[screenSize ?? size ?? MEDIUM];
  const actualNoHorizontalPadding =
    screenNoHorizontalPadding ?? noHorizontalPadding;
  const actualNoVerticalPadding = screenNoVerticalPadding ?? noVerticalPadding;
  const actualTestId = screenTestId ?? testId;
  const nestedContext = {
    currentScreen,
    setScreenTitle,
    setScreenSize,
    setScreenTestId,
    setScreenNoHorizontalPadding,
    setScreenNoVerticalPadding,
  };

  const boundariesElement =
    document.getElementById(POPOVER_BOUNDARY_ELEMENT_ID) || undefined;

  return (
    <Popper
      referenceElement={anchorElement}
      placement={placement || PopoverPlacement.BOTTOM_START}
      modifiers={{
        computeStyle: {
          // Uncommenting the line below will disable gpuAcceleration which
          // fixes the 'blurriness' of popover content on different zoom levels
          // in chrome
          gpuAcceleration: false,
        },
        keepTogether: {
          enabled: false,
        },
        preventOverflow: {
          boundariesElement,
          padding: PopoverPopperDefaultPaddingUnit,
        },
      }}
    >
      {({ ref, style, scheduleUpdate }) => {
        schedulePopperUpdateRef.current = scheduleUpdate;

        return (
          <Portal zIndex={PopoverPortalDefaultZIndex}>
            <section
              className={cx(
                styles[PopoverClasses.POPOVER],
                dangerous_className,
                // This is silly, but we want to 'opt out' of the global click handler
                // logic. Specifically for trying to handle SPA transitions on raw <a>
                // tags. Any 'new' links should be using RouterLink.
                'js-react-root',
              )}
              ref={(node) => containerRefCallback(node, ref)}
              data-test-id={actualTestId}
              style={{
                ...style,
                width: actualSize,
              }}
              {...{ [ELEVATION_ATTR]: nextElevation }}
            >
              <PopoverContext.Provider value={nestedContext}>
                {actualTitle ? (
                  <PopoverHeader
                    hasBackButton={hasBackButton}
                    onBack={onBack}
                    onHide={onHide}
                    ref={headerRef}
                  >
                    {actualTitle}
                  </PopoverHeader>
                ) : null}
                <PopoverContent
                  noHorizontalPadding={actualNoHorizontalPadding}
                  noTopPadding={actualNoVerticalPadding || !!actualTitle}
                  noBottomPadding={actualNoVerticalPadding}
                  ref={contentRef}
                  maxHeight={contentMaxHeight}
                >
                  {children}
                </PopoverContent>
              </PopoverContext.Provider>
            </section>
          </Portal>
        );
      }}
    </Popper>
  );
};
